import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react";
import type { ReactNode } from "react";
import { Heading } from "../atoms/Heading";
import { IconButton } from "../atoms/IconButton";
import { cn } from "../lib/cn";

export type AlertTone = "info" | "warning" | "error" | "success";

const TONE_CLASSES: Record<AlertTone, string> = {
	info: "bg-statusInfo/10 border-statusInfo/30 text-foreground",
	warning: "bg-statusWarning/10 border-statusWarning/30 text-foreground",
	error: "bg-destructive/10 border-destructive/30 text-foreground",
	success: "bg-statusSuccess/10 border-statusSuccess/30 text-foreground",
};

const TONE_ICONS: Record<AlertTone, ReactNode> = {
	info: <Info className="h-4 w-4 text-statusInfo" aria-hidden="true" />,
	warning: (
		<AlertTriangle className="h-4 w-4 text-statusWarning" aria-hidden="true" />
	),
	error: (
		<AlertCircle className="h-4 w-4 text-destructive" aria-hidden="true" />
	),
	success: (
		<CheckCircle className="h-4 w-4 text-statusSuccess" aria-hidden="true" />
	),
};

export interface AlertBannerProps {
	tone: AlertTone;
	title?: string;
	children: ReactNode;
	onDismiss?: () => void;
	className?: string;
}

function AlertBanner({
	tone,
	title,
	children,
	onDismiss,
	className,
}: AlertBannerProps): ReactNode {
	return (
		<div
			role="alert"
			className={cn(
				"flex gap-3 rounded-md border p-4",
				TONE_CLASSES[tone],
				className,
			)}
		>
			<div className="shrink-0 pt-0.5">{TONE_ICONS[tone]}</div>
			<div className="flex-1">
				{title && (
					<Heading size="sm" className="mb-1">
						{title}
					</Heading>
				)}
				<div className="text-sm">{children}</div>
			</div>
			{onDismiss && (
				<IconButton
					aria-label="Dismiss"
					size="sm"
					variant="ghost"
					onClick={onDismiss}
					className="shrink-0 self-start"
				>
					<X className="h-4 w-4" aria-hidden="true" />
				</IconButton>
			)}
		</div>
	);
}
AlertBanner.displayName = "AlertBanner";

export default AlertBanner;
export { AlertBanner };
